/** Shift scheduling mutations for the Распоред feature (owner/manager via RLS).
 *  All writes go through PostgREST accessors in @gym/api-client and invalidate
 *  the ['staff_shifts'] query family so every view refetches. */
import { createShift, deleteShift, updateShift } from '@gym/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../../lib/auth.js';
import { supabase } from '../../../lib/supabase.js';

export interface ShiftMutationInput {
  staffId: string;
  locationId?: string | null;
  scheduledStart: string;
  scheduledEnd: string;
}

export function useCreateShift() {
  const qc = useQueryClient();
  const { claims } = useAuth();
  return useMutation({
    mutationFn: async (input: ShiftMutationInput) => {
      const { data, error } = await createShift(supabase, claims?.tenant_id ?? '', input);
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff_shifts'] }),
  });
}

export function useUpdateShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; input: Partial<ShiftMutationInput> }) => {
      const { data, error } = await updateShift(supabase, vars.id, vars.input);
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff_shifts'] }),
  });
}

export function useDeleteShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await deleteShift(supabase, id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff_shifts'] }),
  });
}
